import React, { useState, useEffect } from 'react';
import { contactApi } from '../services/api';

const DEFAULT_SETTINGS = {
  badgeText: 'Get In Touch',
  heading: 'We’d Love to Hear from You',
  subheading: 'Have a question or need assistance? Reach out to our care team anytime.',
  phone: '+91 98765 43210',
  email: 'care@psychmitra.com',
  address: 'Bandra West, Mumbai, MH, India',
  workingHours: 'Mon – Sat: 9:00 AM – 8:00 PM',
  privacyNote: '100% Confidential & Secure',
  mapEmbedUrl:
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3771.441113038628!2d72.82583837584102!3d19.055325982144795!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c9410830616d%3A0x111b63353dbb1216!2sBandra%20West%2C%20Mumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin',
  mapDirectionsUrl: 'https://maps.google.com/?q=Bandra+West+Mumbai',
  isEnabled: true,
};

function ContactPage() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', text: '' });

  useEffect(() => {
    let isMounted = true;
    const loadSettings = async () => {
      try {
        const res = await contactApi.getPublicSettings();
        if (isMounted && res?.success && res.data) {
          setSettings(res.data);
        }
      } catch (err) {
        console.debug('Contact settings loaded default fallback:', err);
      }
    };
    loadSettings();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFeedback({ type: '', text: '' });

    try {
      const res = await contactApi.submitEnquiry(formData);
      setFeedback({
        type: 'success',
        text: res?.message || 'Thank you! Your message has been sent. We will get back to you shortly.',
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.message || 'Failed to send message. Please try again or call us directly.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-modern-page">
      {/* Ambient Decorative Blurs */}
      <div className="contact-ambient-blur blur-1"></div>
      <div className="contact-ambient-blur blur-2"></div>

      {/* Hero Header */}
      <section className="contact-hero">
        <div className="container text-center">
          <span className="contact-pill">✨ {settings.badgeText || 'Get In Touch'}</span>
          <h1 className="contact-heading">
            {settings.heading ? (
              settings.heading
            ) : (
              <>
                We’d Love to <span className="text-gradient">Hear from You</span>
              </>
            )}
          </h1>
          <p className="contact-lead">{settings.subheading || 'Have a question or need assistance? Reach out to our care team anytime.'}</p>
        </div>
      </section>

      {/* 3 Quick Contact Cards */}
      <section className="contact-cards-wrap">
        <div className="container">
          <div className="row g-3 justify-content-center">
            {/* Phone / Call */}
            <div className="col-lg-4 col-md-6">
              <a href={`tel:${(settings.phone || '').replace(/\s+/g, '')}`} className="quick-card card-phone">
                <div className="card-icon phone-icon">
                  <i className="fas fa-phone-alt"></i>
                </div>
                <div className="card-info">
                  <span className="card-lbl">Call Anytime</span>
                  <h4>{settings.phone || '+91 98765 43210'}</h4>
                  <p>{settings.workingHours || 'Mon – Sat: 9:00 AM – 8:00 PM'}</p>
                </div>
              </a>
            </div>

            {/* Email */}
            <div className="col-lg-4 col-md-6">
              <a href={`mailto:${settings.email || 'care@psychmitra.com'}`} className="quick-card card-email">
                <div className="card-icon email-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <div className="card-info">
                  <span className="card-lbl">Send Email</span>
                  <h4>{settings.email || 'care@psychmitra.com'}</h4>
                  <p>Response within 24 hours</p>
                </div>
              </a>
            </div>

            {/* Location / Address */}
            <div className="col-lg-4 col-md-6">
              <a href="#map-location" className="quick-card card-location">
                <div className="card-icon location-icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <div className="card-info">
                  <span className="card-lbl">Visit Centre</span>
                  <h4>{settings.address || 'Bandra West, Mumbai'}</h4>
                  <p>In-Person & Hybrid Consultations</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Form & Info Section */}
      <section className="contact-main-form-section">
        <div className="container">
          <div className="form-glass-card">
            <div className="row g-0">
              {/* Left Info Column */}
              <div className="col-lg-5 form-info-side">
                <div className="info-box-inner">
                  <h3>Let’s Connect</h3>
                  <p className="info-sub">
                    Fill out the form and our coordinator will respond to guide you with the best care option.
                  </p>

                  <div className="info-list">
                    <div className="info-row">
                      <div className="row-icon">
                        <i className="fas fa-phone-alt"></i>
                      </div>
                      <div>
                        <h6>Helpline Number</h6>
                        <span>{settings.phone || '+91 98765 43210'}</span>
                      </div>
                    </div>

                    <div className="info-row">
                      <div className="row-icon">
                        <i className="fas fa-envelope"></i>
                      </div>
                      <div>
                        <h6>Email Address</h6>
                        <span>{settings.email || 'care@psychmitra.com'}</span>
                      </div>
                    </div>

                    <div className="info-row">
                      <div className="row-icon">
                        <i className="fas fa-map-marker-alt"></i>
                      </div>
                      <div>
                        <h6>Office Location</h6>
                        <span>{settings.address || 'Bandra West, Mumbai, MH, India'}</span>
                      </div>
                    </div>

                    <div className="info-row">
                      <div className="row-icon">
                        <i className="fas fa-shield-alt"></i>
                      </div>
                      <div>
                        <h6>Privacy Ethics</h6>
                        <span>{settings.privacyNote || '100% Confidential & Secure'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="info-bottom-btn">
                    <a href={`tel:${(settings.phone || '').replace(/\s+/g, '')}`} className="call-quick-link">
                      <i className="fas fa-phone-alt"></i> Call Us Directly
                    </a>
                  </div>
                </div>
              </div>

              {/* Right Form Column */}
              <div className="col-lg-7 form-input-side">
                <form onSubmit={handleSubmit} className="modern-form">
                  <h4 className="form-title">Send a Message</h4>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="field-label">Your Name *</label>
                      <input
                        type="text"
                        name="name"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="field-input"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="field-label">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="field-input"
                      />
                    </div>

                    <div className="col-12">
                      <label className="field-label">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="Your phone number"
                        value={formData.phone}
                        onChange={handleChange}
                        className="field-input"
                      />
                    </div>

                    <div className="col-12">
                      <label className="field-label">Message *</label>
                      <textarea
                        name="message"
                        rows="4"
                        placeholder="How can we help you?"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        className="field-input field-textarea"
                      ></textarea>
                    </div>

                    {feedback.text && (
                      <div className="col-12">
                        <div className={`form-alert ${feedback.type}`}>
                          <i
                            className={`fas ${
                              feedback.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'
                            }`}
                          ></i>
                          <span>{feedback.text}</span>
                        </div>
                      </div>
                    )}

                    <div className="col-12 mt-3">
                      <button type="submit" disabled={loading} className="form-btn">
                        {loading ? 'Sending...' : 'Send Message'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Embedded Location Map */}
      <section className="contact-map-wrap" id="map-location">
        <div className="container">
          <div className="map-wrapper">
            <div className="map-header">
              <div>
                <span className="map-badge">Find Us</span>
                <h3 className="map-title">PsychMitra Centre, Mumbai</h3>
              </div>
              <a
                href={settings.mapDirectionsUrl || 'https://maps.google.com/?q=Bandra+West+Mumbai'}
                target="_blank"
                rel="noopener noreferrer"
                className="directions-btn"
              >
                <i className="fas fa-directions"></i> Get Directions
              </a>
            </div>

            <div className="map-container">
              <iframe
                title="PsychMitra Location"
                src={
                  settings.mapEmbedUrl ||
                  'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3771.441113038628!2d72.82583837584102!3d19.055325982144795!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c9410830616d%3A0x111b63353dbb1216!2sBandra%20West%2C%20Mumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin'
                }
                width="100%"
                height="340"
                style={{ border: 0, borderRadius: '18px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .contact-modern-page {
          background: #faf7fd;
          position: relative;
          padding-top: 150px;
          padding-bottom: 80px;
          overflow: hidden;
        }

        .contact-ambient-blur {
          position: absolute;
          border-radius: 50%;
          filter: blur(90px);
          pointer-events: none;
          opacity: 0.5;
          z-index: 0;
        }

        .blur-1 {
          top: 0;
          left: -10%;
          width: 450px;
          height: 450px;
          background: radial-gradient(circle, rgba(124, 75, 185, 0.15) 0%, transparent 70%);
        }

        .blur-2 {
          top: 40%;
          right: -10%;
          width: 450px;
          height: 450px;
          background: radial-gradient(circle, rgba(144, 114, 200, 0.12) 0%, transparent 70%);
        }

        /* Hero */
        .contact-hero {
          position: relative;
          z-index: 1;
          padding: 10px 0 35px;
        }

        .contact-pill {
          display: inline-block;
          background: #ede3fa;
          color: #7c4bb9;
          font-size: 13px;
          font-weight: 700;
          padding: 6px 18px;
          border-radius: 20px;
          margin-bottom: 14px;
        }

        .contact-heading {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 800;
          color: #21123e;
          margin-bottom: 10px;
        }

        .text-gradient {
          background: linear-gradient(135deg, #7c4bb9 0%, #9e6ed9 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .contact-lead {
          font-size: 16px;
          color: #6a627a;
          max-width: 500px;
          margin: 0 auto;
        }

        /* 3 Quick Cards */
        .contact-cards-wrap {
          position: relative;
          z-index: 1;
          margin-bottom: 45px;
        }

        .quick-card {
          display: flex;
          align-items: center;
          gap: 16px;
          background: #ffffff;
          border: 1.5px solid #eee5fb;
          border-radius: 18px;
          padding: 20px 22px;
          text-decoration: none;
          color: inherit;
          box-shadow: 0 8px 25px rgba(124, 75, 185, 0.04);
          transition: all 0.28s ease;
          height: 100%;
        }

        .quick-card:hover {
          transform: translateY(-5px);
          border-color: #c4b5fd;
          box-shadow: 0 14px 35px rgba(124, 75, 185, 0.12);
          color: inherit;
        }

        .card-icon {
          width: 50px;
          height: 50px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }

        .phone-icon {
          background: #f2ecfd;
          color: #7c4bb9;
        }

        .email-icon {
          background: #fef3e9;
          color: #f97316;
        }

        .location-icon {
          background: #eef2ff;
          color: #4f46e5;
        }

        .card-lbl {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          color: #8b819f;
          display: block;
        }

        .card-info h4 {
          font-size: 16.5px;
          font-weight: 700;
          color: #21123e;
          margin: 2px 0 0;
        }

        .card-info p {
          font-size: 13px;
          color: #6a627a;
          margin: 2px 0 0;
        }

        /* Form Glass Card */
        .contact-main-form-section {
          position: relative;
          z-index: 1;
          margin-bottom: 50px;
        }

        .form-glass-card {
          background: #ffffff;
          border-radius: 24px;
          overflow: hidden;
          border: 1.5px solid #eee5fb;
          box-shadow: 0 15px 45px rgba(124, 75, 185, 0.07);
          max-width: 1000px;
          margin: 0 auto;
        }

        /* Left Info Side */
        .form-info-side {
          background: linear-gradient(145deg, #7c4bb9 0%, #5b2896 100%);
          color: #ffffff;
          padding: 44px 36px;
        }

        .info-box-inner h3 {
          font-size: 24px;
          font-weight: 800;
          color: #ffffff;
          margin-bottom: 8px;
        }

        .info-sub {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.82);
          line-height: 1.5;
          margin-bottom: 30px;
        }

        .info-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin-bottom: 35px;
        }

        .info-row {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .row-icon {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          color: #ffffff;
          flex-shrink: 0;
        }

        .info-row h6 {
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: rgba(255, 255, 255, 0.65);
          margin: 0;
        }

        .info-row span {
          font-size: 14px;
          font-weight: 600;
          color: #ffffff;
        }

        .call-quick-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.35);
          color: #ffffff;
          font-size: 13.5px;
          font-weight: 700;
          padding: 11px 22px;
          border-radius: 12px;
          text-decoration: none;
          transition: background-color 0.2s, transform 0.2s;
        }

        .call-quick-link:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-2px);
          color: #ffffff;
        }

        /* Right Form Side */
        .form-input-side {
          padding: 44px 38px;
          background: #ffffff;
        }

        .form-title {
          font-size: 20px;
          font-weight: 800;
          color: #21123e;
          margin-bottom: 22px;
        }

        .field-label {
          font-size: 13px;
          font-weight: 600;
          color: #382a50;
          margin-bottom: 6px;
          display: block;
        }

        .field-input {
          width: 100%;
          height: 46px;
          padding: 0 16px;
          background: #faf7fe;
          border: 1.5px solid #e7ddf6;
          border-radius: 12px;
          font-size: 14px;
          color: #21123e;
          outline: none;
          transition: border-color 0.2s, background-color 0.2s;
        }

        .field-input:focus {
          background: #ffffff;
          border-color: #7c4bb9;
        }

        .field-textarea {
          height: auto;
          min-height: 100px;
          padding: 12px 16px;
          resize: vertical;
        }

        .form-alert {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          border-radius: 10px;
          font-size: 13.5px;
          font-weight: 500;
        }

        .form-alert.success {
          background: #ecfdf5;
          color: #065f46;
          border: 1px solid #a7f3d0;
        }

        .form-alert.error {
          background: #fef2f2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        .form-btn {
          width: 100%;
          height: 48px;
          background: #7c4bb9;
          color: #ffffff;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.25s ease;
          box-shadow: 0 6px 18px rgba(124, 75, 185, 0.25);
        }

        .form-btn:hover:not(:disabled) {
          background: #6a3aa8;
          transform: translateY(-2px);
          box-shadow: 0 10px 24px rgba(124, 75, 185, 0.35);
        }

        .form-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* Map Section */
        .contact-map-wrap {
          position: relative;
          z-index: 1;
        }

        .map-wrapper {
          background: #ffffff;
          border-radius: 24px;
          padding: 30px;
          border: 1.5px solid #eee5fb;
          box-shadow: 0 15px 45px rgba(124, 75, 185, 0.05);
          max-width: 1000px;
          margin: 0 auto;
        }

        .map-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 20px;
        }

        .map-badge {
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #7c4bb9;
          display: block;
          margin-bottom: 2px;
        }

        .map-title {
          font-size: 22px;
          font-weight: 800;
          color: #21123e;
          margin: 0;
        }

        .directions-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #7c4bb9;
          color: #ffffff;
          font-size: 13.5px;
          font-weight: 700;
          padding: 9px 18px;
          border-radius: 10px;
          text-decoration: none;
          transition: background-color 0.2s;
        }

        .directions-btn:hover {
          background: #6a3aa8;
          color: #ffffff;
        }

        .map-container {
          border-radius: 18px;
          overflow: hidden;
        }

        @media (max-width: 991px) {
          .contact-modern-page {
            padding-top: 120px;
          }
          .form-info-side, .form-input-side {
            padding: 30px 22px;
          }
          .map-wrapper {
            padding: 20px 16px;
          }
        }
      `}</style>
    </div>
  );
}

export default ContactPage;
